import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sparkles, Loader2 } from "lucide-react";
import { toast } from "sonner";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function googleLogin() {
  const redirectUrl = window.location.origin + "/auth-callback";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

export function AuthShell({ title, subtitle, children }) {
  return (
    <div className="min-h-screen bg-cream mfm-grain flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center justify-center gap-2 mb-8">
          <span className="w-10 h-10 rounded-xl bg-amber flex items-center justify-center text-white"><Sparkles className="w-5 h-5" /></span>
          <span className="font-display text-xl font-bold text-slateink">Made For Me Studio</span>
        </Link>
        <div className="bg-white rounded-3xl border border-slate-100 shadow-[0_10px_40px_-12px_rgba(0,0,0,0.1)] p-8">
          <h1 className="font-display text-2xl font-bold text-slateink">{title}</h1>
          <p className="text-slate-500 text-sm mt-1 mb-6">{subtitle}</p>
          {children}
        </div>
      </div>
    </div>
  );
}

export function GoogleButton() {
  return (
    <button type="button" onClick={googleLogin} data-testid="google-login-btn"
      className="w-full flex items-center justify-center gap-2 border border-slate-200 rounded-full py-2.5 font-medium text-slate-700 hover:bg-slate-50 transition-colors">
      <img alt="" src="https://www.svgrepo.com/show/475656/google-color.svg" className="w-4 h-4" />
      Continue with Google
    </button>
  );
}

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || "/account";

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await login(email, password);
      toast.success("Welcome back!");
      navigate(u.role === "admin" ? "/admin" : from, { replace: true });
    } catch (err) {
      toast.error(apiError(err));
    } finally { setBusy(false); }
  };

  return (
    <AuthShell title="Welcome back" subtitle="Sign in to track orders and download your keepsakes.">
      <form onSubmit={submit} className="space-y-4">
        <div>
          <Label htmlFor="email">Email</Label>
          <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email" className="mt-1" placeholder="you@example.com" />
        </div>
        <div>
          <div className="flex items-center justify-between">
            <Label htmlFor="password">Password</Label>
            <Link to="/forgot-password" className="text-xs text-amber font-medium">Forgot?</Link>
          </div>
          <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password" className="mt-1" placeholder="••••••••" />
        </div>
        <Button type="submit" disabled={busy} data-testid="login-submit" className="w-full rounded-full bg-amber hover:bg-amber-600 text-white font-semibold">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign in"}
        </Button>
      </form>
      <div className="flex items-center gap-3 my-5"><div className="h-px bg-slate-200 flex-1" /><span className="text-xs text-slate-400">or</span><div className="h-px bg-slate-200 flex-1" /></div>
      <GoogleButton />
      <p className="text-center text-sm text-slate-500 mt-6">New here? <Link to="/register" className="text-amber font-semibold">Create an account</Link></p>
    </AuthShell>
  );
}
